<template>
  <div class="ats-integration">
    <vue-content-loading v-if="loading " :height="300" :speed="1">
      <template v-if="!isMobile">
        <rect x="0" y="0" rx="4" ry="4" width="32%" height="40" />
        <rect x="33%" y="0" rx="4" ry="4" width="32%" height="40" />
        <rect x="66%" y="0" rx="4" ry="4" width="32%" height="40" />
        <rect x="0" y="50" rx="4" ry="4" width="32%" height="40" />
        <rect x="33%" y="50" rx="4" ry="4" width="32%" height="40" />
        <rect x="66%" y="50" rx="4" ry="4" width="32%" height="40" />
      </template>
      <template v-else>
        <rect x="0" y="0" rx="4" ry="4" width="100%" height="120" />
        <rect x="0" y="136" rx="4" ry="4" width="100%" height="120" />
        <rect x="0" y="272" rx="4" ry="4" width="100%" height="120" />
      </template>
    </vue-content-loading>
    <div v-else-if="integrations.length" class="ats-integration__items">
      <div
        class="ats-integration-item"
        v-for="integration in integrations"
        :key="integration.name"
      >
        <div class="ats-integration-item__content">
          <img
            :src="integration.logo_url"
            :alt="integration.name"
            width="48"
            height="48"
          />
          <div class="ats-integration-item__text">
            <p class="ats-integration-item__title">
              {{ integration.name }}
              <i
                class="icon-info cursor-pointer ml-1"
                v-if="integration.token_instructions"
                @click="showModal(integration)"
              />
            </p>
          </div>
          <input
            id="switch"
            type="checkbox"
            :checked="integration.connected"
            class="switch-checkbox"
            :disabled="loading"
            @click.prevent="handleConnect($event, integration)"
          />
        </div>
      </div>
    </div>

    <b-modal v-model="modal" id="modal-center" centered hide-footer modal-class="token-modal" @hide="hideModal">
      <template slot="modal-header">
        <h3>{{ resource['unifiedTokenInstructionsModalTitle'] }}</h3>
        <i class="icon-close custom-modal-close cursor-pointer" @click="hideModal" />
      </template>
      <div class="d-flex flex-column w-100">
        <div class="d-flex">
          <ul class="intructions-list">
            <template v-for="instruction in activeIntegration?.token_instructions" >
              <li v-if="instruction !== ''" :key="instruction">
                <p v-html="instruction"></p>
              </li>
            </template>
          </ul>
        </div>
        <div class="button-area">
          <b-button
            name="okay"
            class="bg-button gap-2 d-flex align-items-center"
            @click="hideModal()"
          >
            <span>{{ resource['unifiedTokenInstructionsModalButton'] }}</span>
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="disconnectModal" id="modal-center" centered hide-footer modal-class="disconnect-modal" @hide="hideDisconnectModal">
      <template slot="modal-header">
        <h3>{{ resource['unifiedDisconnectModalTitle'] }}</h3>
        <i class="icon-close custom-modal-close cursor-pointer" @click="hideDisconnectModal" />
      </template>
      <div class="d-flex flex-column w-100">
        <div class="d-flex">
          <p>{{ resource['unifiedDisconnectModalText'] }}</p>
        </div>
        <div class="button-area">
          <b-button
            name="cancel"
            class="border-button gap-2 d-flex align-items-center"
            @click="hideDisconnectModal()"
          >
            <span>{{ resource['unifiedDisconnectModalCancelButton'] }}</span>
          </b-button>
          <b-button
            name="disconnect"
            class="bg-button gap-2 d-flex align-items-center"
            @click="handleDisconnect()"
          >
            <span>{{ resource['unifiedDisconnectModalSubmitButton'] }}</span>
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import hrvenueService from '../../services/hrvenue.service';
import { showNotification } from '../../static/helpers';

export default {
  name: 'UnifiedIntegration',

  props: {
    workspace_id: {
      type: String,
      required: true,
    },
    scopes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      integrations: [],
      loading: false,
      modal: false,
      activeIntegration: null,
      disconnectModal: false,
      isMobile: false,
    };
  },

  created() {
    this.fetchIntegrations();
  },

  mounted() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 1048;
    },
    showModal(integration) {
      this.activeIntegration = integration;
      this.modal = true;
    },
    hideModal() {
      this.activeIntegration = null;
      this.modal = false;
    },
    hideDisconnectModal() {
      this.activeIntegration = null;
      this.disconnectModal = false;
    },
    async fetchIntegrations() {
      try {
        this.loading = true;
        const response = await hrvenueService.getUnifiedIntegrations();
        this.integrations = response || [];
      } catch (error) {
        showNotification(
          this.resource['label.error'],
          error.message,
          'error',
        );
      } finally {
        this.loading = false;
      }
    },

    async handleDisconnect() {
      try {
        this.loading = true;
        const response = await hrvenueService.disconnectIntegration(
          this.activeIntegration.type,
        );
        if (response) {
          this.fetchIntegrations();
        }
      } catch (error) {
        showNotification(
          this.resource['label.error'],
          error.message,
          'error',
        );
      } finally {
        this.loading = false;
        this.activeIntegration = null;
        this.disconnectModal = false;
      }
    },

    async handleConnect(e, integration) {
      if (integration.connected) {
        e.target.checked = integration.connected;
        this.activeIntegration = integration;
        this.disconnectModal = true;
        return;
      }
      try {
        this.loading = true;
        const response = await hrvenueService.createAuthLink(
          integration.type,
        );

        if (!response.body) {
          window.location.href = response;
        }
      } catch (error) {
        showNotification(
          this.resource['label.error'],
          error.message,
          'error',
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/style/starter.scss";

.ats-integration {
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  margin: 0 auto;

  &__title {
    display: flex;
    width: 100%;
    color: #353535;
    font-family: Ubuntu;
    font-size: 22px;
    font-weight: 500;
    line-height: 28px;
  }
  &__description {
    font-family: Ubuntu;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    color: #353535;
    text-align: center;
  }
  &__items {
    display: flex;
    width: 100%;
    align-items: stretch;
    gap: 16px;
    align-self: stretch;
    flex-wrap: wrap;
    @media only screen and (max-width: map-get($grid-breakpoints, "lg")) {
      gap: 16px
    }
  }
  &__button {
    border-radius: 4px;
    width: 214px;
    padding: 8px 22px;
    background: #FF8D06 !important;
    color: #FFFFFF;
    text-decoration: none;
    font-family: Ubuntu;
    font-size: 16px;
    font-weight: 500;
    line-height: 26px;
    letter-spacing: 0.46px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transition: all 0.2s ease-in-out;
    &:hover{
      background: #FF7415 !important;
    }

    &:disabled {
      background: #E4E4E4 !important;
      color: #353535 !important;
    }

    @media only screen and (max-width: map-get($grid-breakpoints, "lg")) {
      width: 100%;
    }
  }

}

.ats-integration-item {
  display: flex;
  flex-wrap: wrap;
  min-width: 304px;
  width: calc(100% / 3 - 11px);
  flex-direction: column;
  align-items: center;
  padding: 24px 24px 24px 16px;
  gap: 6px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  position: relative;

  @media only screen and (max-width: map-get($grid-breakpoints, "lg")) {
    width: calc(100% / 2 - 8px);
    min-width: auto;
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
    width: 100%;
    min-width: auto;
  }

  &__content {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    flex: 1;

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      flex-wrap: wrap;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    flex: 1 0 0;
  }

  &__title {
    color: #353535;
    font-family: Ubuntu;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    flex: 1;

    @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
      font-size: 14px;
    }
  }

  &__description {
    color: #878787;
    font-family: Ubuntu;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin-bottom: 0;
    flex: 1;
  }

  &__button {
    border-radius: 50px;
    width: 214px;
    padding: 8px 22px;
    background: transparent !important;
    color: #353535;
    border: 1px solid #353535;
    width: fit-content;
    min-width: fit-content;
    text-decoration: none;
    font-family: Ubuntu;
    font-size: 16px;
    font-weight: 500;
    line-height: 26px;
    letter-spacing: 0.46px;
    transition: all 0.2s ease-in-out;

    &:hover {
      background: #353535 !important;
      color: #FFFFFF !important;
    }

    &:disabled {
      background: #E4E4E4 !important;
      color: #353535 !important;
      cursor: not-allowed !important;
      pointer-events: auto !important;
    }
  }

  &__badge {
    position: absolute;
    height: 20px;
    right: 37px;
    bottom: 0px;
    display: flex;
    padding: 2px 8px;
    justify-content: center;
    align-items: center;
    gap: 2px;
    border-radius: 2px 2px 0 0;
    border: 1px solid #e4e4e4;
    background: #eeeeee;
  }

  &__badge-text {
    color: #353535;
    font-feature-settings: "liga" off, "clig" off;
    font-family: Ubuntu;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
  }
}

.bg-button {
  display: flex;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 114px;
  border: 1px solid #ff8d06;
  background: #ff8d06;
  transition: all 0.2s ease-in-out;

  span {
    color: #fff;
    text-align: center;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    padding: 0 8px;
  }

  &.disabled {
    border-color: transparent;
  }

  &:hover {
    background: #FF7415;
  }
}

.border-button {
  display: flex;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 114px;
  border: 1px solid #ff8d06;
  background: transparent;
  transition: all 0.2s ease-in-out;

  span {
    color: #ff8d06;
    text-align: center;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    padding: 0 8px;
  }

  &.disabled {
    border-color: transparent;
  }

  &:hover {
    background: #ff8d06;
    color: #fff !important;

    span {
      color: #fff !important;
    }
  }
}

.intructions-list {
  ::v-deep a {
    color: #FF8D06;
    text-decoration: underline;
  }

  li {
    margin-bottom: 16px;
  }
}

input[type="checkbox"] {
    position: relative;
    opacity: 1;
  }

.switch-checkbox {
  --toggle-size: 16px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 42px;
  height: 22px;
  background: -webkit-gradient(
      linear,
      left top,
      right top,
      color-stop(50%, #C3C3C3),
      color-stop(50%, #FF8D06)
    )
    no-repeat;
  background: -o-linear-gradient(left, #C3C3C3 50%, #FF8D06 50%) no-repeat;
  background: linear-gradient(to right, #C3C3C3 50%, #FF8D06 50%) no-repeat;
  background-size: 205%;
  background-position: 0;
  -webkit-transition: 0.4s;
  -o-transition: 0.4s;
  transition: 0.4s;
  border-radius: 99em;
  position: relative;
  cursor: pointer;
  font-size: var(--toggle-size);

  &:focus-visible {
    outline: 0;
  }
}

.switch-checkbox::before {
  content: "";
  width: 13px;
  height: 13px;
  position: absolute;
  top: 4.5px;
  left: 5px;
  background: -webkit-gradient(
      linear,
      left top,
      right top,
      color-stop(50%, #fff),
      color-stop(50%, #fff)
    )
    no-repeat;
  background: -o-linear-gradient(left, #fff 50%, #fff 50%) no-repeat;
  background: linear-gradient(to right, #fff 50%, #fff 50%) no-repeat;
  background-size: 205%;
  background-position: 100%;
  border-radius: 50%;
  -webkit-transition: 0.4s;
  -o-transition: 0.4s;
  transition: 0.4s;
}

.switch-checkbox:checked::before {
  left: calc(100% - 16px - 2px);
  background-position: 0;
}

.switch-checkbox:checked {
  background-position: 100%;
}

::v-deep .disconnect-modal,
::v-deep .token-modal {
  .modal-dialog {
    width: 466px;
    max-width: 466px;

    @media only screen and (max-width: map-get($grid-breakpoints, "lg")) {
      width: 90%;
      max-width: 90%;
      margin: 0 auto;
    }
  }
  .modal-header {
    padding: 12px 12px 12px 24px !important;
  }
  .modal-body {
    padding: 16px 24px !important;

    ul {
      padding-left: 16px;
    }

    p {
      color: var(--Text-Colors-Primary-Gray-Text, #333);
      font-feature-settings: 'liga' off, 'clig' off;
      /* Typography/Body/3 - Regular */
      font-family: Roboto;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin-bottom: 0;
    }
  }

  .button-area {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 24px;
    width: 100%;

    @media only screen and (max-width: map-get($grid-breakpoints, "lg")) {
      flex-direction: column;
      flex: 1 1 100%;
    }
  }
}
</style>
